import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useRequestFeature } from '@/lib/api/feedback'
import { useTranslation } from 'react-i18next'

const schema = z.object({
  featureName: z.string().min(1, 'Feature name is required'),
  description: z.string().optional(),
})

type FormData = z.infer<typeof schema>

interface RequestFeatureDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function RequestFeatureDialog({
  open,
  onOpenChange,
}: RequestFeatureDialogProps) {
  const { t } = useTranslation('navigation')
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const requestMutation = useRequestFeature()

  const onSubmit = async (data: FormData) => {
    try {
      const payload = {
        featureName: data.featureName.trim(),
        description: data.description?.trim() || undefined,
      }

      await requestMutation.mutateAsync(payload)

      reset()
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to send feature request:', error)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('requestFeatureTitle')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="featureName">{t('featureName')} *</Label>
            <Input
              id="featureName"
              placeholder={t('featureNamePlaceholder')}
              {...register('featureName')}
            />
            {errors.featureName && (
              <p className="text-sm text-destructive">
                {errors.featureName.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t('featureDescription')}</Label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Textarea
                  id="description"
                  placeholder={t('featureDescriptionPlaceholder')}
                  rows={4}
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={requestMutation.isPending}>
              {requestMutation.isPending ? t('sending') : t('sendRequest')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
